import React, { useEffect } from 'react';
import pdfIconRaw from '../../../../static/icons/pdf.svg?raw';

const LINK_BASE_CLASS = 'sitePageLink';
const LINK_ICON_CLASS = 'sitePageLinkWithIcon';
const DATA_VARIANT_ATTRIBUTE = 'data-sitepage-link-variant';
const CSS_ICON_PROPERTY = '--sitepage-link-icon';

interface LinkIconConfig {
    readonly variant: string;
    readonly iconAsset: string | { readonly src: string } | null;
}

const LINK_ICON_CONFIG: Record<string, LinkIconConfig> = {
    pdf: {
        variant: 'pdf',
        iconAsset:
            pdfIconRaw && pdfIconRaw.length > 0
                ? `data:image/svg+xml,${encodeURIComponent(pdfIconRaw)}`
                : null,
    },
};

function resolveIconUrl(
    iconAsset: string | { readonly src: string } | null | undefined
): string | null {
    if (!iconAsset) {
        return null;
    }

    if (typeof iconAsset === 'string') {
        return iconAsset;
    }

    if (typeof iconAsset === 'object' && typeof iconAsset.src === 'string') {
        return iconAsset.src;
    }

    return null;
}

function getLinkExtension(href: string): string | null {
    if (!href) {
        return null;
    }

    let url: URL | null = null;

    try {
        const base =
            typeof window !== 'undefined' && window.location ? window.location.origin : undefined;
        url = new URL(href, base);
    } catch {
        return null;
    }

    const pathname = url.pathname ?? '';
    const lastSegment = pathname.split('/').pop();

    if (!lastSegment) {
        return null;
    }

    const dotIndex = lastSegment.lastIndexOf('.');
    if (dotIndex <= 0 || dotIndex === lastSegment.length - 1) {
        return null;
    }

    return lastSegment.slice(dotIndex + 1).toLowerCase();
}

function resetLinkAttributes(anchor: HTMLAnchorElement) {
    anchor.classList.add(LINK_BASE_CLASS);
    anchor.classList.remove(LINK_ICON_CLASS);
    anchor.removeAttribute(DATA_VARIANT_ATTRIBUTE);
    anchor.style.removeProperty(CSS_ICON_PROPERTY);
}

function applyIconEnhancement(anchor: HTMLAnchorElement, config: LinkIconConfig) {
    const iconUrl = resolveIconUrl(config.iconAsset);
    if (!iconUrl) {
        return;
    }

    anchor.classList.add(LINK_ICON_CLASS);
    anchor.setAttribute(DATA_VARIANT_ATTRIBUTE, config.variant);
    anchor.style.setProperty(CSS_ICON_PROPERTY, `url("${iconUrl}")`);
}

export function enhanceSitePageLinks(root: HTMLElement) {
    if (typeof window === 'undefined') {
        return;
    }

    const anchors = root.querySelectorAll<HTMLAnchorElement>('a[href]');

    anchors.forEach((anchor) => {
        resetLinkAttributes(anchor);

        const href = anchor.getAttribute('href') ?? '';
        const extension = getLinkExtension(href);
        if (!extension) {
            return;
        }

        const iconConfig = LINK_ICON_CONFIG[extension];
        if (!iconConfig) {
            return;
        }

        applyIconEnhancement(anchor, iconConfig);
    });
}

export function useSitePageLinkEnhancements(
    ref: React.RefObject<HTMLElement | null>,
    dependencyKey?: unknown
) {
    useEffect(() => {
        const element = ref.current;
        if (!element || typeof window === 'undefined') {
            return;
        }

        let rafId: number | null = null;
        let timeoutId: number | null = null;

        const scheduleEnhancements = () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
                rafId = null;
            }

            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }

            if (typeof window.requestAnimationFrame === 'function') {
                rafId = window.requestAnimationFrame(() => {
                    rafId = null;
                    enhanceSitePageLinks(element);
                });
                return;
            }

            timeoutId = window.setTimeout(() => {
                timeoutId = null;
                enhanceSitePageLinks(element);
            }, 0);
        };

        enhanceSitePageLinks(element);

        const observer =
            typeof window.MutationObserver !== 'undefined'
                ? new window.MutationObserver((mutations) => {
                      const hasChildListMutation = mutations.some(
                          (mutation) => mutation.type === 'childList'
                      );
                      if (hasChildListMutation) {
                          scheduleEnhancements();
                      }
                  })
                : null;

        if (observer) {
            observer.observe(element, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }

            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }

            observer?.disconnect();
        };
    }, [ref, dependencyKey]);
}

export function applySitePageLinkEnhancements(root: HTMLElement) {
    enhanceSitePageLinks(root);
}
